// Service Package Mock Data

export interface CPTCode {
  id: string
  code: string
  description: string
  category: string
}

export interface ICD10Code {
  id: string
  code: string
  description: string
  category: string
}

export interface Payer {
  id: string
  name: string
  type: 'commercial' | 'medicare' | 'medicaid' | 'workers_comp' | 'auto'
  networkStatus: 'in_network' | 'out_of_network' | 'pending'
}

export interface Specialty {
  id: string
  name: string
  category: string
  description?: string
}

export interface BodyRegion {
  id: string
  name: string
  category: string
  description?: string
}

export interface State {
  id: string
  name: string
  code: string
}

export interface ReportType {
  id: string
  name: string
  description: string
  required: boolean
}

export interface ExportFormat {
  id: string
  name: string
  extension: string
  description: string
}

// Mock CPT Codes
export const cptCodes: CPTCode[] = [
  {
    id: '1',
    code: '97110',
    description: 'Therapeutic Exercise',
    category: 'Physical Therapy',
  },
  {
    id: '2',
    code: '97112',
    description: 'Neuromuscular Reeducation',
    category: 'Physical Therapy',
  },
  {
    id: '3',
    code: '97113',
    description: 'Aquatic Therapy',
    category: 'Physical Therapy',
  },
  {
    id: '4',
    code: '97116',
    description: 'Gait Training',
    category: 'Physical Therapy',
  },
  {
    id: '5',
    code: '97140',
    description: 'Manual Therapy',
    category: 'Physical Therapy',
  },
  {
    id: '6',
    code: '97150',
    description: 'Group Therapeutic Exercise',
    category: 'Physical Therapy',
  },
  {
    id: '7',
    code: '97161',
    description: 'Physical Therapy Evaluation - Low Complexity',
    category: 'Evaluation',
  },
  {
    id: '8',
    code: '97162',
    description: 'Physical Therapy Evaluation - Moderate Complexity',
    category: 'Evaluation',
  },
  {
    id: '9',
    code: '97163',
    description: 'Physical Therapy Evaluation - High Complexity',
    category: 'Evaluation',
  },
  {
    id: '10',
    code: '97164',
    description: 'Physical Therapy Re-evaluation',
    category: 'Evaluation',
  },
  {
    id: '11',
    code: '97165',
    description: 'Occupational Therapy Evaluation - Low Complexity',
    category: 'Evaluation',
  },
  {
    id: '12',
    code: '97166',
    description: 'Occupational Therapy Evaluation - Moderate Complexity',
    category: 'Evaluation',
  },
  {
    id: '13',
    code: '97167',
    description: 'Occupational Therapy Evaluation - High Complexity',
    category: 'Evaluation',
  },
  {
    id: '14',
    code: '97168',
    description: 'Occupational Therapy Re-evaluation',
    category: 'Evaluation',
  },
  {
    id: '15',
    code: '97530',
    description: 'Therapeutic Activities',
    category: 'Occupational Therapy',
  },
  {
    id: '16',
    code: '97533',
    description: 'Sensory Integration',
    category: 'Occupational Therapy',
  },
  {
    id: '17',
    code: '97535',
    description: 'Self Care Management Training',
    category: 'Occupational Therapy',
  },
  {
    id: '18',
    code: '97537',
    description: 'Community Work Reintegration',
    category: 'Occupational Therapy',
  },
  {
    id: '19',
    code: '97542',
    description: 'Wheelchair Management Training',
    category: 'Occupational Therapy',
  },
  {
    id: '20',
    code: '97750',
    description: 'Physical Performance Test',
    category: 'Testing',
  },
]

// Mock ICD-10 Codes
export const icd10Codes: ICD10Code[] = [
  {
    id: '1',
    code: 'M79.3',
    description: 'Panniculitis, unspecified',
    category: 'Musculoskeletal',
  },
  {
    id: '2',
    code: 'M25.561',
    description: 'Pain in right knee',
    category: 'Musculoskeletal',
  },
  {
    id: '3',
    code: 'M25.562',
    description: 'Pain in left knee',
    category: 'Musculoskeletal',
  },
  {
    id: '4',
    code: 'M54.5',
    description: 'Low back pain',
    category: 'Musculoskeletal',
  },
  {
    id: '5',
    code: 'M79.1',
    description: 'Myalgia',
    category: 'Musculoskeletal',
  },
  {
    id: '6',
    code: 'M25.511',
    description: 'Pain in right shoulder',
    category: 'Musculoskeletal',
  },
  {
    id: '7',
    code: 'M25.512',
    description: 'Pain in left shoulder',
    category: 'Musculoskeletal',
  },
  {
    id: '8',
    code: 'M79.2',
    description: 'Neuralgia and neuritis, unspecified',
    category: 'Neurological',
  },
  {
    id: '9',
    code: 'G43.909',
    description:
      'Migraine, unspecified, not intractable, without status migrainosus',
    category: 'Neurological',
  },
  {
    id: '10',
    code: 'G25.9',
    description: 'Extrapyramidal and movement disorder, unspecified',
    category: 'Neurological',
  },
  {
    id: '11',
    code: 'F32.9',
    description: 'Major depressive disorder, single episode, unspecified',
    category: 'Mental Health',
  },
  {
    id: '12',
    code: 'F41.9',
    description: 'Anxiety disorder, unspecified',
    category: 'Mental Health',
  },
  {
    id: '13',
    code: 'F84.0',
    description: 'Autistic disorder',
    category: 'Developmental',
  },
  {
    id: '14',
    code: 'F90.9',
    description: 'Attention-deficit hyperactivity disorder, unspecified type',
    category: 'Developmental',
  },
  {
    id: '15',
    code: 'R29.3',
    description: 'Abnormal posture',
    category: 'Symptoms',
  },
  { id: '16', code: 'R26.0', description: 'Ataxic gait', category: 'Symptoms' },
  {
    id: '17',
    code: 'R26.1',
    description: 'Paralytic gait',
    category: 'Symptoms',
  },
  {
    id: '18',
    code: 'R26.89',
    description: 'Other abnormalities of gait and mobility',
    category: 'Symptoms',
  },
  {
    id: '19',
    code: 'Z87.891',
    description: 'Personal history of other specified diseases',
    category: 'History',
  },
  {
    id: '20',
    code: 'Z51.11',
    description: 'Antineoplastic chemotherapy',
    category: 'Treatment',
  },
]

// Mock Payers
export const payers: Payer[] = [
  {
    id: '1',
    name: 'Blue Cross Blue Shield',
    type: 'commercial',
    networkStatus: 'in_network',
  },
  { id: '2', name: 'Aetna', type: 'commercial', networkStatus: 'in_network' },
  { id: '3', name: 'Cigna', type: 'commercial', networkStatus: 'in_network' },
  {
    id: '4',
    name: 'UnitedHealthcare',
    type: 'commercial',
    networkStatus: 'in_network',
  },
  { id: '5', name: 'Humana', type: 'commercial', networkStatus: 'in_network' },
  {
    id: '6',
    name: 'Kaiser Permanente',
    type: 'commercial',
    networkStatus: 'in_network',
  },
  {
    id: '7',
    name: 'Medicare Part B',
    type: 'medicare',
    networkStatus: 'in_network',
  },
  { id: '8', name: 'Medicaid', type: 'medicaid', networkStatus: 'in_network' },
  {
    id: '9',
    name: 'Workers Compensation',
    type: 'workers_comp',
    networkStatus: 'in_network',
  },
  {
    id: '10',
    name: 'Auto Insurance',
    type: 'auto',
    networkStatus: 'in_network',
  },
  {
    id: '11',
    name: 'Tricare',
    type: 'commercial',
    networkStatus: 'in_network',
  },
  { id: '12', name: 'Ambetter', type: 'commercial', networkStatus: 'pending' },
  {
    id: '13',
    name: 'Molina Healthcare',
    type: 'commercial',
    networkStatus: 'in_network',
  },
  {
    id: '14',
    name: 'Centene',
    type: 'commercial',
    networkStatus: 'in_network',
  },
  {
    id: '15',
    name: 'Independence Blue Cross',
    type: 'commercial',
    networkStatus: 'in_network',
  },
]

// Mock Specialties
export const specialties: Specialty[] = [
  {
    id: '1',
    name: 'Orthopedic Physical Therapy',
    category: 'Physical Therapy',
    description: 'Musculoskeletal conditions and injuries',
  },
  {
    id: '2',
    name: 'Neurological Physical Therapy',
    category: 'Physical Therapy',
    description: 'Stroke, brain injury, and neurological conditions',
  },
  {
    id: '3',
    name: 'Pediatric Physical Therapy',
    category: 'Physical Therapy',
    description: 'Children and developmental conditions',
  },
  {
    id: '4',
    name: 'Geriatric Physical Therapy',
    category: 'Physical Therapy',
    description: 'Aging-related conditions and fall prevention',
  },
  {
    id: '5',
    name: 'Sports Physical Therapy',
    category: 'Physical Therapy',
    description: 'Athletic injuries and performance enhancement',
  },
  {
    id: '6',
    name: 'Cardiopulmonary Physical Therapy',
    category: 'Physical Therapy',
    description: 'Heart and lung conditions',
  },
  {
    id: '7',
    name: 'Vestibular Rehabilitation',
    category: 'Physical Therapy',
    description: 'Balance and dizziness disorders',
  },
  {
    id: '8',
    name: 'Pelvic Health Physical Therapy',
    category: 'Physical Therapy',
    description: 'Pelvic floor dysfunction and conditions',
  },
  {
    id: '9',
    name: 'Occupational Therapy - Pediatrics',
    category: 'Occupational Therapy',
    description: 'Children with developmental delays',
  },
  {
    id: '10',
    name: 'Occupational Therapy - Hand Therapy',
    category: 'Occupational Therapy',
    description: 'Upper extremity and hand conditions',
  },
  {
    id: '11',
    name: 'Occupational Therapy - Mental Health',
    category: 'Occupational Therapy',
    description: 'Mental health and psychosocial conditions',
  },
  {
    id: '12',
    name: 'Speech-Language Pathology',
    category: 'Speech Therapy',
    description: 'Communication and swallowing disorders',
  },
  {
    id: '13',
    name: 'Applied Behavior Analysis',
    category: 'Behavioral Therapy',
    description: 'Autism and behavioral interventions',
  },
  {
    id: '14',
    name: 'Cognitive Behavioral Therapy',
    category: 'Mental Health',
    description: 'Anxiety, depression, and mental health conditions',
  },
  {
    id: '15',
    name: 'Massage Therapy',
    category: 'Manual Therapy',
    description: 'Soft tissue mobilization and relaxation',
  },
]

// Mock Body Regions
export const bodyRegions: BodyRegion[] = [
  {
    id: '1',
    name: 'Head & Neck',
    category: 'Upper Body',
    description: 'Cervical spine and surrounding structures',
  },
  {
    id: '2',
    name: 'Shoulder',
    category: 'Upper Body',
    description: 'Shoulder joint and surrounding muscles',
  },
  {
    id: '3',
    name: 'Elbow',
    category: 'Upper Body',
    description: 'Elbow joint and forearm',
  },
  {
    id: '4',
    name: 'Wrist & Hand',
    category: 'Upper Body',
    description: 'Wrist, hand, and fingers',
  },
  {
    id: '5',
    name: 'Chest',
    category: 'Torso',
    description: 'Rib cage and chest muscles',
  },
  {
    id: '6',
    name: 'Upper Back',
    category: 'Torso',
    description: 'Thoracic spine and upper back muscles',
  },
  {
    id: '7',
    name: 'Lower Back',
    category: 'Torso',
    description: 'Lumbar spine and lower back muscles',
  },
  {
    id: '8',
    name: 'Hip',
    category: 'Lower Body',
    description: 'Hip joint and surrounding muscles',
  },
  {
    id: '9',
    name: 'Knee',
    category: 'Lower Body',
    description: 'Knee joint and surrounding structures',
  },
  {
    id: '10',
    name: 'Ankle & Foot',
    category: 'Lower Body',
    description: 'Ankle, foot, and toes',
  },
  {
    id: '11',
    name: 'Pelvis',
    category: 'Torso',
    description: 'Pelvic region and pelvic floor',
  },
  {
    id: '12',
    name: 'Full Body',
    category: 'General',
    description: 'Comprehensive full-body treatment',
  },
]

// Mock States
export const states: State[] = [
  { id: '1', name: 'Alabama', code: 'AL' },
  { id: '2', name: 'Alaska', code: 'AK' },
  { id: '3', name: 'Arizona', code: 'AZ' },
  { id: '4', name: 'Arkansas', code: 'AR' },
  { id: '5', name: 'California', code: 'CA' },
  { id: '6', name: 'Colorado', code: 'CO' },
  { id: '7', name: 'Connecticut', code: 'CT' },
  { id: '8', name: 'Delaware', code: 'DE' },
  { id: '9', name: 'Florida', code: 'FL' },
  { id: '10', name: 'Georgia', code: 'GA' },
  { id: '11', name: 'Hawaii', code: 'HI' },
  { id: '12', name: 'Idaho', code: 'ID' },
  { id: '13', name: 'Illinois', code: 'IL' },
  { id: '14', name: 'Indiana', code: 'IN' },
  { id: '15', name: 'Iowa', code: 'IA' },
  { id: '16', name: 'Kansas', code: 'KS' },
  { id: '17', name: 'Kentucky', code: 'KY' },
  { id: '18', name: 'Louisiana', code: 'LA' },
  { id: '19', name: 'Maine', code: 'ME' },
  { id: '20', name: 'Maryland', code: 'MD' },
  { id: '21', name: 'Massachusetts', code: 'MA' },
  { id: '22', name: 'Michigan', code: 'MI' },
  { id: '23', name: 'Minnesota', code: 'MN' },
  { id: '24', name: 'Mississippi', code: 'MS' },
  { id: '25', name: 'Missouri', code: 'MO' },
  { id: '26', name: 'Montana', code: 'MT' },
  { id: '27', name: 'Nebraska', code: 'NE' },
  { id: '28', name: 'Nevada', code: 'NV' },
  { id: '29', name: 'New Hampshire', code: 'NH' },
  { id: '30', name: 'New Jersey', code: 'NJ' },
  { id: '31', name: 'New Mexico', code: 'NM' },
  { id: '32', name: 'New York', code: 'NY' },
  { id: '33', name: 'North Carolina', code: 'NC' },
  { id: '34', name: 'North Dakota', code: 'ND' },
  { id: '35', name: 'Ohio', code: 'OH' },
  { id: '36', name: 'Oklahoma', code: 'OK' },
  { id: '37', name: 'Oregon', code: 'OR' },
  { id: '38', name: 'Pennsylvania', code: 'PA' },
  { id: '39', name: 'Rhode Island', code: 'RI' },
  { id: '40', name: 'South Carolina', code: 'SC' },
  { id: '41', name: 'South Dakota', code: 'SD' },
  { id: '42', name: 'Tennessee', code: 'TN' },
  { id: '43', name: 'Texas', code: 'TX' },
  { id: '44', name: 'Utah', code: 'UT' },
  { id: '45', name: 'Vermont', code: 'VT' },
  { id: '46', name: 'Virginia', code: 'VA' },
  { id: '47', name: 'Washington', code: 'WA' },
  { id: '48', name: 'West Virginia', code: 'WV' },
  { id: '49', name: 'Wisconsin', code: 'WI' },
  { id: '50', name: 'Wyoming', code: 'WY' },
]

// Mock Report Types
export const reportTypes: ReportType[] = [
  {
    id: '1',
    name: 'Initial Evaluation Report',
    description: 'Comprehensive initial assessment',
    required: false,
  },
  {
    id: '2',
    name: 'Progress Note',
    description: 'Weekly progress documentation',
    required: false,
  },
  {
    id: '3',
    name: 'Discharge Summary',
    description: 'Final treatment summary',
    required: false,
  },
  {
    id: '4',
    name: 'Re-evaluation Report',
    description: 'Updated assessment and goals',
    required: false,
  },
  {
    id: '5',
    name: 'SOAP Notes',
    description: 'Prior authorization documentation',
    required: false,
  },
]

// Mock Export Formats
export const exportFormats: ExportFormat[] = [
  {
    id: '1',
    name: 'PDF',
    extension: '.pdf',
    description: 'Portable Document Format',
  },
  {
    id: '2',
    name: 'Word Document',
    extension: '.docx',
    description: 'Microsoft Word Document',
  },
  {
    id: '3',
    name: 'Excel Spreadsheet',
    extension: '.xlsx',
    description: 'Microsoft Excel Spreadsheet',
  },
  {
    id: '4',
    name: 'CSV',
    extension: '.csv',
    description: 'Comma-Separated Values',
  },
  {
    id: '5',
    name: 'JSON',
    extension: '.json',
    description: 'JavaScript Object Notation',
  },
  {
    id: '6',
    name: 'XML',
    extension: '.xml',
    description: 'Extensible Markup Language',
  },
  {
    id: '7',
    name: 'Text File',
    extension: '.txt',
    description: 'Plain Text File',
  },
  {
    id: '8',
    name: 'HTML',
    extension: '.html',
    description: 'HyperText Markup Language',
  },
]

// Mock Session Includes
export const sessionIncludes = [
  'Initial Assessment',
  'Treatment Plan Development',
  'Hands-on Therapy',
  'Exercise Prescription',
  'Patient Education',
  'Progress Monitoring',
  'Home Exercise Program',
  'Equipment Training',
  'Pain Management',
  'Functional Training',
  'Balance Training',
  'Strength Training',
  'Flexibility Training',
  'Coordination Training',
  'Endurance Training',
]

// Mock Modalities
export const modalities = [
  {
    id: '1',
    value: 'in_person',
    name: 'In-Person',
    description: 'Face-to-face therapy sessions',
  },
  // {
  //   id: '2',
  //   value: 'telehealth',
  //   name: 'Telehealth',
  //   description: 'Remote therapy sessions via video',
  // },
  // {
  //   id: '3',
  //   value: 'hybrid',
  //   name: 'Hybrid',
  //   description: 'Combination of in-person and telehealth',
  // },
]

// Mock Disciplines
export const disciplines = [
  {
    id: '1',
    value: 'PT',
    name: 'Physical Therapist',
    description: 'Licensed PT',
  },
  {
    id: '2',
    value: 'PTA',
    name: 'Physical Therapist Assistant',
    description: 'Licensed PTA',
  },
  {
    id: '3',
    value: 'OT',
    name: 'Occupational Therapist',
    description: 'Licensed OT',
  },
  {
    id: '4',
    value: 'OTA',
    name: 'Occupational Therapist Assistant',
    description: 'Licensed OTA',
  },
  {
    id: '5',
    value: 'SLP',
    name: 'Speech-Language Pathologist',
    description: 'Licensed SLP',
  },
]

// Mock Languages
export const languages = [
  { id: '1', name: 'English', code: 'en' },
  { id: '2', name: 'Spanish', code: 'es' },
  { id: '3', name: 'French', code: 'fr' },
  { id: '4', name: 'German', code: 'de' },
  { id: '5', name: 'Mandarin Chinese', code: 'zh' },
  { id: '6', name: 'Arabic', code: 'ar' },
  { id: '7', name: 'Hindi', code: 'hi' },
  { id: '8', name: 'Portuguese', code: 'pt' },
  { id: '9', name: 'Russian', code: 'ru' },
  { id: '10', name: 'Japanese', code: 'ja' },
  { id: '11', name: 'Korean', code: 'ko' },
  { id: '12', name: 'Italian', code: 'it' },
  { id: '13', name: 'Vietnamese', code: 'vi' },
  { id: '14', name: 'Tagalog', code: 'tl' },
  { id: '15', name: 'American Sign Language', code: 'asl' },
]

// Mock Service Packages for Listing
export const mockServicePackages = [
  {
    id: '1',
    serviceTitle: 'Comprehensive Physical Therapy Package',
    discipline: 'PT',
    modality: ['in_person', 'telehealth'],
    shortDescription:
      'Complete physical therapy program for recovery from injuries and surgeries with both in-person and telehealth options.',
    privatePayRate: 125,
    sessionLengthMin: 60,
    status: 'published',
    specialties: ['Orthopedic Physical Therapy', 'Sports Physical Therapy'],
    bodyRegions: ['Knee', 'Shoulder', 'Lower Back'],
    keywords: ['post-surgery', 'rehabilitation', 'sports injury'],
    updatedAt: '2025-09-15T14:30:00Z',
    createdAt: '2025-09-01T10:15:00Z',
  },
  {
    id: '2',
    serviceTitle: 'Pediatric Speech Therapy',
    discipline: 'SLP',
    modality: ['telehealth'],
    shortDescription:
      'Speech therapy services for children ages 3-12 focusing on articulation, language development, and fluency disorders.',
    privatePayRate: 95,
    sessionLengthMin: 45,
    status: 'published',
    specialties: ['Speech-Language Pathology'],
    bodyRegions: ['Head & Neck'],
    keywords: ['pediatric', 'speech delay', 'articulation'],
    updatedAt: '2025-09-20T09:45:00Z',
    createdAt: '2025-08-25T13:20:00Z',
  },
  {
    id: '3',
    serviceTitle: 'Senior Mobility Program',
    discipline: 'PT',
    modality: ['in_person'],
    shortDescription:
      'Specialized physical therapy for seniors focusing on balance, fall prevention, and maintaining independence in daily activities.',
    privatePayRate: 110,
    sessionLengthMin: 45,
    status: 'draft',
    specialties: ['Geriatric Physical Therapy'],
    bodyRegions: ['Full Body', 'Hip', 'Ankle & Foot'],
    keywords: ['senior', 'balance', 'fall prevention'],
    updatedAt: '2025-09-25T16:10:00Z',
    createdAt: '2025-09-20T11:30:00Z',
  },
  {
    id: '4',
    serviceTitle: 'Hand Therapy Rehabilitation',
    discipline: 'OT',
    modality: ['in_person', 'hybrid'],
    shortDescription:
      'Specialized occupational therapy for hand injuries, surgeries, and conditions affecting hand function and dexterity.',
    privatePayRate: 135,
    sessionLengthMin: 60,
    status: 'published',
    specialties: ['Occupational Therapy - Hand Therapy'],
    bodyRegions: ['Wrist & Hand'],
    keywords: ['hand injury', 'dexterity', 'fine motor skills'],
    updatedAt: '2025-09-10T08:20:00Z',
    createdAt: '2025-08-15T15:45:00Z',
  },
  {
    id: '5',
    serviceTitle: 'Neurological Rehabilitation',
    discipline: 'PT',
    modality: ['hybrid'],
    shortDescription:
      'Comprehensive therapy for patients recovering from stroke, brain injury, or living with neurological conditions.',
    privatePayRate: 150,
    sessionLengthMin: 75,
    status: 'draft',
    specialties: ['Neurological Physical Therapy'],
    bodyRegions: ['Full Body', 'Head & Neck'],
    keywords: ['stroke', 'brain injury', 'neurological'],
    updatedAt: '2025-09-28T10:05:00Z',
    createdAt: '2025-09-22T14:30:00Z',
  },
]
